
import { useState } from "react";
import type { User } from "@supabase/supabase-js";
import { SignatureInput } from "@/components/SignatureInput";
import { useSignatureGeneration } from "@/hooks/useSignatureGeneration";
import { useSignatures } from "@/hooks/useSignatures";
import { useCredits } from "@/hooks/useCredits";
import { toast } from "sonner";

type SignatureStyle = "vector_illustration/line_art" | "icon/outline";

interface CalligraphyFormProps {
  user: User | null;
  generatedSvg: string | null;
  setGeneratedSvg: (svg: string | null) => void;
}

export const CalligraphyForm = ({ user, generatedSvg, setGeneratedSvg }: CalligraphyFormProps) => {
  const [text, setText] = useState("");
  const [style, setStyle] = useState<SignatureStyle>("vector_illustration/line_art");
  const { saveSignature } = useSignatures(user);
  const { credits, isLoading: isLoadingCredits, deductCredits } = useCredits(user);
  
  const { generateSignature, loading } = useSignatureGeneration(user, async (svg) => {
    setGeneratedSvg(svg);
    if (user && text) {
      await saveSignature(text, svg);
    }
  }, deductCredits);

  const handleTextChange = (value: string) => {
    // Only allow Latin characters and common punctuation
    if (/^[a-zA-Z\s.,'-]*$/.test(value)) {
      setText(value);
    } else {
      toast.error("Please use only Latin characters (A-Z, a-z)");
    }
  };

  const handleGenerate = () => {
    if (!text.trim()) return;
    generateSignature(text, style);
  };

  return (
    <div className="space-y-4">
      <SignatureInput
        value={text}
        onChange={handleTextChange}
        onSubmit={handleGenerate}
        user={user}
        generatedSvg={generatedSvg}
        loading={loading || isLoadingCredits}
        style={style}
        onStyleChange={setStyle}
      />
    </div>
  );
};
